import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Clock, Loader2, Flame, History, CalendarCheck, HelpCircle, ScrollText, AlertTriangle } from "lucide-react";
import { ROOBET_LINK } from "../mock";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const RulesDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button
        data-testid="leaderboard-rules-button"
        className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[#473c7d] bg-[#251e44] text-white text-sm font-bold hover:bg-[#3d3370] transition-colors"
      >
        <ScrollText size={16} className="text-[#edbb23]" /> RULES
      </button>
    </DialogTrigger>
    <DialogContent className="bg-[#2a2249] border-[#473c7d] text-white max-w-lg" data-testid="leaderboard-rules-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-2xl font-black">
          <ScrollText size={22} className="text-[#edbb23]" /> Leaderboard Rules
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <p className="text-sm text-[#b0a9d0]">
          Wagers are weighted based on the game's RTP (Return To Player):
        </p>
        <ul className="space-y-3">
          <li className="flex items-start gap-3 rounded-xl border border-[#473c7d] bg-[#251e44] px-4 py-3">
            <span className="shrink-0 mt-0.5 rounded-md bg-[#edbb23] text-[#251e44] text-xs font-black px-2 py-1">100%</span>
            <span className="text-sm text-[#e7e3f5]">Games with RTP of <strong>97% or lower</strong> &rarr; 100% of wager is counted</span>
          </li>
          <li className="flex items-start gap-3 rounded-xl border border-[#473c7d] bg-[#251e44] px-4 py-3">
            <span className="shrink-0 mt-0.5 rounded-md bg-[#5b5baf] text-white text-xs font-black px-2 py-1">50%</span>
            <span className="text-sm text-[#e7e3f5]">Games with RTP between <strong>97.01% and 98.99%</strong> &rarr; 50% of wager is counted</span>
          </li>
          <li className="flex items-start gap-3 rounded-xl border border-[#473c7d] bg-[#251e44] px-4 py-3">
            <span className="shrink-0 mt-0.5 rounded-md bg-[#473c7d] text-white text-xs font-black px-2 py-1">10%</span>
            <span className="text-sm text-[#e7e3f5]">Games with RTP of <strong>99% and over</strong> &rarr; 10% of wager is counted</span>
          </li>
        </ul>
        <div className="flex items-start gap-3 rounded-xl border border-[#edbb23]/40 bg-[#edbb23]/10 px-4 py-3">
          <AlertTriangle size={18} className="shrink-0 mt-0.5 text-[#edbb23]" />
          <p className="text-sm text-[#e7e3f5]">
            Any abuse found by <strong className="text-[#edbb23]">Broer</strong> or Roobet may result in your prize being forfeit.
          </p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RankBadge = ({ p, size = "w-9 h-9", hideOnError = false }) => {
  const [err, setErr] = useState(false);
  if (p.rankLevelImage && !err) {
    return (
      <img
        src={p.rankLevelImage}
        alt=""
        className={`${size} object-contain shrink-0`}
        onError={() => setErr(true)}
      />
    );
  }
  if (hideOnError) return null;
  return (
    <div className={`${size} rounded-full bg-[#edbb23] text-[#251e44] flex items-center justify-center text-sm font-black shrink-0`}>
      {p.rank}
    </div>
  );
};

const Countdown = ({ endIso }) => {
  const [time, setTime] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    if (!endIso) return;
    const end = new Date(endIso);
    const tick = () => {
      const diff = Math.max(0, end - new Date());
      setTime({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endIso]);

  const box = (val, label) => (
    <div className="flex flex-col items-center">
      <div className="w-11 sm:w-14 md:w-16 h-11 sm:h-14 md:h-16 rounded-lg sm:rounded-xl border border-[#473c7d] bg-[#251e44] text-white flex items-center justify-center text-base sm:text-xl md:text-2xl font-black tabular-nums">
        {String(val).padStart(2, "0")}
      </div>
      <span className="mt-1.5 sm:mt-2 text-[9px] sm:text-[10px] font-bold tracking-widest text-[#8f88b8]">{label}</span>
    </div>
  );

  return (
    <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
      {box(time.d, "DAYS")}
      <span className="text-lg sm:text-2xl font-black text-[#edbb23] -mt-4">:</span>
      {box(time.h, "HRS")}
      <span className="text-lg sm:text-2xl font-black text-[#edbb23] -mt-4">:</span>
      {box(time.m, "MIN")}
      <span className="text-lg sm:text-2xl font-black text-[#edbb23] -mt-4">:</span>
      {box(time.s, "SEC")}
    </div>
  );
};

const PodiumCard = ({ p }) => {
  const elevated = p.rank === 1;
  return (
    <div className={`relative rounded-2xl border border-[#473c7d] bg-[#342b60] p-4 sm:p-6 pt-6 sm:pt-8 text-center ${elevated ? "md:-mt-6 md:scale-[1.04]" : ""} transition-transform duration-300 hover:-translate-y-1`}>
      <div className="flex justify-center mb-3">
        <img
          src={`/assets/rank${p.rank}.png`}
          alt={`Rank ${p.rank}`}
          className={`h-16 sm:h-20 w-auto object-contain drop-shadow-xl ${elevated ? "animate-float-slow" : ""}`}
        />
      </div>
      <div className="flex items-center justify-center gap-2">
        <RankBadge p={p} size="w-8 h-8" />
        <h3 className="text-lg font-extrabold text-white truncate">{p.username}</h3>
      </div>
      <p className="text-[11px] font-bold tracking-widest mt-2 text-[#8f88b8]">WAGERED</p>
      <p className="text-xl font-black text-[#edbb23] leading-tight mt-0.5">{money(p.wagered)}</p>
      <p className="text-xs text-[#9a92c0] leading-tight mt-0.5">{money(p.totalWagered)} raw</p>
      <div className="mt-5 pt-4 border-t border-[#473c7d]">
        <p className="text-[11px] font-bold tracking-widest text-[#8f88b8]">PRIZE</p>
        <p className="text-2xl font-black text-[#edbb23] mt-0.5">{money(p.prize)}</p>
      </div>
    </div>
  );
};

const Leaderboard = () => {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(null);
  const [dataByMonth, setDataByMonth] = useState({});
  const [loading, setLoading] = useState(true);

  // load month list once
  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/leaderboard/months?limit=6`)
      .then((res) => {
        if (!mounted) return;
        setMonths(res.data || []);
        const cur = (res.data || []).find((m) => m.current) || (res.data || [])[0];
        setMonth(cur ? cur.key : null);
      })
      .catch(() => mounted && setMonth("current"));
    return () => { mounted = false; };
  }, []);

  // fetch leaderboard for the selected month
  useEffect(() => {
    if (!month) return;
    if (dataByMonth[month]) { setLoading(false); return; }
    let mounted = true;
    setLoading(true);
    const q = month === "current" ? "" : `&month=${month}`;
    axios
      .get(`${API}/leaderboard?brand=roobet${q}`)
      .then((res) => mounted && setDataByMonth((prev) => ({ ...prev, [month]: res.data })))
      .catch(() => mounted && setDataByMonth((prev) => ({ ...prev, [month]: { players: [] } })))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = month ? dataByMonth[month] : null;
  const isCurrent = data?.isCurrent !== false; // treat unknown as current
  const players = data?.players || [];
  const top3 = players.slice(0, 3);
  const rest = players.slice(3);
  const ordered = top3.length === 3 ? [top3[1], top3[0], top3[2]] : top3;
  const pool = data?.prizePool ?? 0;
  const monthLabel = data?.monthLabel || "";

  return (
    <section id="leaderboard" className="relative py-20 px-5">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="relative max-w-5xl mx-auto">
        {/* month selector */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
          <div className="flex items-center gap-2 text-[#b0a9d0] text-sm font-bold">
            <History size={16} className="text-[#edbb23]" />
            PREVIOUS LEADERBOARDS
          </div>
          <Select value={month || undefined} onValueChange={setMonth}>
            <SelectTrigger className="w-[230px] bg-[#251e44] border-[#473c7d] text-white font-bold h-11 rounded-xl focus:ring-[#edbb23]">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent className="bg-[#342b60] border-[#473c7d] text-white">
              {months.map((m) => (
                <SelectItem
                  key={m.key}
                  value={m.key}
                  className="font-semibold focus:bg-[#5b5baf]/30 focus:text-white cursor-pointer"
                >
                  {m.label}{m.current ? "  (Live)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RulesDialog />
        </div>

        {/* leaderboard box */}
        <div className="relative rounded-3xl border border-[#473c7d] overflow-hidden bg-gradient-to-b from-[#3a3068] to-[#2a2249]">
          {/* header */}
          <div className="text-center px-6 pt-10 pb-8">
            <img src="/assets/roobet-full.png" alt="Roobet" className="h-9 md:h-11 w-auto object-contain mx-auto mb-5" />
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">
              {isCurrent ? "MONTHLY LEADERBOARD" : "FINAL RESULTS"}
            </h2>
            {!isCurrent && monthLabel && (
              <p className="mt-2 text-[#edbb23] font-bold tracking-wide">{monthLabel}</p>
            )}

            {/* countdown (current) or ended badge (past) */}
            {isCurrent ? (
              <div className="inline-flex flex-col items-center gap-4 mt-8 rounded-2xl border border-[#473c7d] bg-[#251e44] px-3 sm:px-6 md:px-10 py-5">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-[#edbb23]" />
                  <span className="text-[11px] font-bold tracking-[0.25em] text-[#8f88b8]">ENDS IN</span>
                </div>
                <Countdown endIso={data?.raceEnd} />
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 mt-8 rounded-xl border border-[#473c7d] bg-[#251e44] px-5 py-3">
                <CalendarCheck size={16} className="text-[#edbb23]" />
                <span className="text-sm font-bold text-[#cfc9e8]">RACE ENDED</span>
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-2">
              <span className="text-xs font-bold tracking-widest text-[#8f88b8]">PRIZE POOL:</span>
              <span className="text-lg font-black text-[#edbb23]">{money(pool)}</span>
            </div>
          </div>

          {/* body */}
          <div className="border-t border-[#473c7d] px-5 md:px-8 py-8">
            {loading || !data ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 size={38} className="animate-spin mb-4 text-[#edbb23]" />
                <p className="text-[#b0a9d0]">Loading leaderboard...</p>
              </div>
            ) : players.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-14">
                <Flame size={40} className="mb-4 text-[#edbb23]" />
                <h3 className="text-2xl font-extrabold text-white">
                  {isCurrent ? "The race is heating up!" : "No results for this period"}
                </h3>
                <p className="mt-3 max-w-md text-[#b0a9d0]">
                  {isCurrent ? (
                    <>No wagers yet this month. Be the first to climb the board with code <span className="text-[#edbb23] font-bold">BROER</span>.</>
                  ) : (
                    <>There were no tracked wagers for {monthLabel}.</>
                  )}
                </p>
              </div>
            ) : (
              <>
                {/* podium - desktop (2-1-3) */}
                <div className="hidden md:grid md:grid-cols-3 gap-6 items-end mb-10">
                  {ordered.map((p) => (
                    <PodiumCard key={p.rank} p={p} />
                  ))}
                </div>

                {/* podium - mobile (#1 on top, #2 & #3 side by side) */}
                <div className="md:hidden mb-8">
                  {top3[0] && (
                    <div className="mb-4 max-w-[260px] mx-auto">
                      <PodiumCard p={top3[0]} />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {top3[1] && <PodiumCard p={top3[1]} />}
                    {top3[2] && <PodiumCard p={top3[2]} />}
                  </div>
                </div>

                {/* rest */}
                {rest.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3 px-1">
                      <span className="text-xs font-black tracking-widest text-[#8f88b8]">
                        RANKS {rest[0].rank} - {rest[rest.length - 1].rank}
                      </span>
                      <span className="text-[11px] text-[#8f88b8]">{isCurrent ? "Updated regularly" : "Final"}</span>
                    </div>
                    <div className="rounded-2xl border border-[#473c7d] bg-[#251e44] overflow-hidden">
                      {rest.map((p) => (
                        <div
                          key={p.rank}
                          className="flex items-center gap-4 px-4 md:px-6 py-4 border-b border-[#473c7d]/60 last:border-0 hover:bg-[#3d3370] transition-colors"
                        >
                          <span className="w-5 text-center font-black text-[#8f88b8] text-sm shrink-0">{p.rank}</span>
                          <RankBadge p={p} size="w-9 h-9" />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-white truncate">{p.username}</p>
                            <p className="text-sm font-extrabold text-[#edbb23] leading-tight">
                              {money(p.wagered)} <span className="text-[10px] font-semibold text-[#8f88b8]">weighted</span>
                            </p>
                            <p className="text-xs text-[#9a92c0] leading-tight">
                              {money(p.totalWagered)} <span className="text-[10px]">raw</span>
                            </p>
                          </div>
                          <span className="text-lg font-black text-[#edbb23]">{p.prize ? money(p.prize) : "\u2014"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
          <a
            href={ROOBET_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-extrabold rounded-xl px-8 h-14 transition-colors bg-[#edbb23] hover:bg-[#f4cf52] text-[#251e44]"
          >
            JOIN THE RACE ON ROOBET <img src="/assets/roobet-dark.png" alt="" className="w-5 h-5 object-contain" />
          </a>
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 font-extrabold rounded-xl px-8 h-14 transition-colors border border-[#5b5baf] text-white hover:bg-[#5b5baf]/25"
          >
            <HelpCircle size={18} className="text-[#edbb23]" /> HOW DOES IT WORK?
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Leaderboard;
